import * as yup from 'yup';

const DANGEROUS_INPUT_PATTERNS = [
  /<script/i,
  /javascript:/i,
  /on\w+\s*=/i,
  /<iframe/i,
  /<object/i,
  /<embed/i,
  /\bor\b\s+1\s*=\s*1/i,
  /\bunion\b\s+\bselect\b/i,
  /\bdrop\b\s+\btable\b/i,
  /;\s*--/,
  /\/\*/,
  /\*\//,
  /\bxp_/i,
  /\bsp_/i,
];

const COMMON_PASSWORDS = [
  'password',
  '12345678',
  'qwerty',
  'abc123',
  'password1',
  'password123',
  '1234567890',
  'letmein',
  'welcome',
  'monkey',
];

const hasControlCharacters = (value: string): boolean => {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 32 || (code >= 127 && code <= 159)) {
      return true;
    }
  }
  return false;
};

const containsDangerousInput = (value: string): boolean => {
  if (!value) {
    return false;
  }
  return DANGEROUS_INPUT_PATTERNS.some((pattern) => pattern.test(value));
};

const hasUnsafeInput = (value: string): boolean => hasControlCharacters(value) || containsDangerousInput(value);

type BaseTextOptions = {
  label: string;
  required?: boolean;
  min?: number;
  max?: number;
  allowedPattern?: RegExp;
  allowedPatternMessage?: string;
};

export const createBaseTextSchema = ({
  label,
  required = true,
  min,
  max,
  allowedPattern,
  allowedPatternMessage,
}: BaseTextOptions): yup.StringSchema<string | undefined> => {
  let schema = yup.string().transform((value) => (typeof value === 'string' ? value.trim() : value));

  if (required) {
    schema = schema.required(`${label} is required`);
  } else {
    schema = schema.optional();
  }

  if (typeof min === 'number') {
    schema = schema.min(min, `${label} must be at least ${min} characters`);
  }

  if (typeof max === 'number') {
    schema = schema.max(max, `${label} must be less than ${max + 1} characters`);
  }

  if (allowedPattern) {
    schema = schema.matches(allowedPattern, allowedPatternMessage || `${label} contains invalid characters`);
  }

  return schema.test('safe-input', `${label} contains dangerous input`, (value) => {
    if (!value) {
      return true;
    }
    return !hasUnsafeInput(value);
  });
};

export const safeEmailSchema = (required = true): yup.StringSchema<string | undefined> => {
  let schema = createBaseTextSchema({ label: 'Email', required, max: 254 })
    .email('Please enter a valid email address')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Please enter a valid email address')
    .test('single-at', 'Invalid email format', (value) => {
      if (!value) return true;
      return (value.match(/@/g) || []).length === 1;
    })
    .test('valid-domain', 'Email domain appears invalid', (value) => {
      if (!value) return true;
      const domain = value.split('@')[1];
      if (!domain) return false;
      return !domain.includes('..') && domain.split('.').length >= 2;
    })
    .test('normalize-email', 'Please enter a valid email address', (value) => {
      if (!value) return true;
      return value === value.trim();
    });

  schema = schema.transform((value) => (typeof value === 'string' ? value.trim().toLowerCase() : value));
  return schema;
};

export const safeNameSchema = (label: string): yup.StringSchema<string | undefined> =>
  createBaseTextSchema({
    label,
    min: 1,
    max: 30,
    allowedPattern: /^[a-zA-Z\s'-]+$/,
    allowedPatternMessage: `${label} can only contain letters, spaces, hyphens, and apostrophes`,
  });

export const getPasswordPolicyErrors = (value: string): string[] => {
  const password = value ?? '';
  const errors: string[] = [];

  if (!password) {
    errors.push('Password is required');
    return errors;
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }
  if (password.length > 128) {
    errors.push('Password must be less than 128 characters');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  if (password.trim() !== password) {
    errors.push('Password cannot contain leading or trailing whitespace');
  }
  if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
    errors.push('Password is too common, please choose a stronger password');
  }
  if (hasUnsafeInput(password)) {
    errors.push('Password contains potentially dangerous input patterns');
  }

  return errors;
};

export const strongPasswordSchema = yup.string()
  .required('Password is required')
  .test('password-policy', function validatePasswordPolicy(value) {
    const errors = getPasswordPolicyErrors(value || '');
    if (errors.length === 0) {
      return true;
    }
    return this.createError({ message: errors[0] });
  });
